import { useTranslation } from 'react-i18next';
import { initialsOf } from '../shared/cellHelpers';
import { StatusChip, type CellTone } from '../shared/cells';
import { CopyableId } from '@/pages/console/audit/CopyableId';
import { isPendingFirstLogin, type KeycloakAccountRow } from '../types';
import type { AccountDetail } from '../accountsApi';

interface Props {
    detail: AccountDetail;
    displayName: string;
    onReset: () => void;
    onLock: () => void;
    onDelete: () => void;
    canReset: boolean;
    canLock: boolean;
    canDelete: boolean;
    lockVisible: boolean;
}

/**
 * EAA-1820 (frame 72): profile hero — avatar, display name, status, username ·
 * email, sub, and the Reset / Lock / Delete actions from the handoff header.
 */
export function AccountProfileHeader({
    detail,
    displayName,
    onReset,
    onLock,
    onDelete,
    canReset,
    canLock,
    canDelete,
    lockVisible,
}: Props) {
    const { t } = useTranslation();

    const tone: CellTone = !detail.enabled ? 'off' : isPendingFirstLogin(detail as KeycloakAccountRow) ? 'warn' : 'ok';
    const statusLabel = !detail.enabled
        ? t('console.accounts.table.statusLocked')
        : isPendingFirstLogin(detail as KeycloakAccountRow)
          ? t('console.accounts.table.statusPendingFirstLogin')
          : t('console.accounts.table.statusActive');

    return (
        <div className="acc-profile-header">
            <span className="acc-profile-header__avatar" aria-hidden="true">
                {initialsOf(displayName)}
            </span>
            <div className="acc-profile-header__main">
                <span className="acc-profile-header__title-row">
                    <h1 className="acc-profile-header__name">{displayName}</h1>
                    <StatusChip tone={tone} label={statusLabel} />
                </span>
                <span className="acc-profile-header__meta">
                    <code>{detail.username}</code>
                    {detail.email && (
                        <>
                            <span className="acc-profile-header__sep">·</span>
                            <span>{detail.email}</span>
                        </>
                    )}
                </span>
                <span className="acc-profile-header__sub">
                    <CopyableId value={detail.sub} />
                </span>
            </div>
            <div className="acc-profile-header__actions">
                {canReset && (
                    <button type="button" className="portal-btn" onClick={onReset}>
                        {t('console.accounts.detailDrawer.resetButton')}
                    </button>
                )}
                {canLock && lockVisible && (
                    <button type="button" className="portal-btn" onClick={onLock}>
                        {t('console.accounts.detailDrawer.lockButton')}
                    </button>
                )}
                {canDelete && (
                    <button type="button" className="portal-btn portal-btn-danger" onClick={onDelete}>
                        {t('console.accounts.detailDrawer.deleteButton')}
                    </button>
                )}
            </div>
        </div>
    );
}

export default AccountProfileHeader;
